import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { defineMessages, useIntl } from 'react-intl';
import AppNav from '../AppNav/AppNav';
import { sharedMessages } from '../../utils/uiMessages';

const DOCX_MIME =
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

const initialProfileForm = {
  title: '',
  translation_style_instructions: '',
  terminology_notes: '',
  glossary_entries: [createEmptyGlossaryEntry()],
  client_report_instructions: '',
};

const initialExampleForm = {
  title: '',
  source_text: '',
  final_translation_text: '',
  notes: '',
};

const messages = defineMessages({
  pageEyebrow: {
    id: 'OA Profiles',
    defaultMessage: 'OA Profiles',
  },
  pageTitle: {
    id: 'Translation and report profiles',
    defaultMessage: 'Translation and report profiles',
  },
  pageNote: {
    id: 'Manage reusable translation instructions, example pairs, and optional DOCX templates for OA output.',
    defaultMessage:
      'Manage reusable translation instructions, example pairs, and optional DOCX templates for OA output.',
  },
  newProfile: {
    id: 'New profile',
    defaultMessage: 'New profile',
  },
  loadingProfiles: {
    id: 'Loading OA profiles…',
    defaultMessage: 'Loading OA profiles…',
  },
  noProfiles: {
    id: 'No OA profiles yet. Create the first one below.',
    defaultMessage: 'No OA profiles yet. Create the first one below.',
  },
  profiles: {
    id: 'Profiles',
    defaultMessage: 'Profiles',
  },
  exampleCount: {
    id: '{count} examples',
    defaultMessage: '{count} examples',
  },
  translationTemplateReady: {
    id: 'Translation template ready',
    defaultMessage: 'Translation template ready',
  },
  translationTemplateMissing: {
    id: 'No translation template',
    defaultMessage: 'No translation template',
  },
  clientReportTemplateReady: {
    id: 'Client report template ready',
    defaultMessage: 'Client report template ready',
  },
  clientReportTemplateMissing: {
    id: 'No client report template',
    defaultMessage: 'No client report template',
  },
  profileEditor: {
    id: 'Profile editor',
    defaultMessage: 'Profile editor',
  },
  selectedProfile: {
    id: 'Selected profile: {title}',
    defaultMessage: 'Selected profile: {title}',
  },
  profileTitle: {
    id: 'Profile title',
    defaultMessage: 'Profile title',
  },
  translationStyleInstructions: {
    id: 'Translation style instructions',
    defaultMessage: 'Translation style instructions',
  },
  preferredTerminology: {
    id: 'Terminology dictionary',
    defaultMessage: 'Terminology dictionary',
  },
  terminologyDictionaryNote: {
    id: 'Store durable term mappings here so they are applied consistently during translation and refinement.',
    defaultMessage:
      'Store durable term mappings here so they are applied consistently during translation and refinement.',
  },
  terminologyNotes: {
    id: 'Additional terminology notes',
    defaultMessage: 'Additional terminology notes',
  },
  glossaryJapanese: {
    id: 'Japanese term',
    defaultMessage: 'Japanese term',
  },
  glossaryEnglish: {
    id: 'Preferred English',
    defaultMessage: 'Preferred English',
  },
  glossaryRule: {
    id: 'Rule',
    defaultMessage: 'Rule',
  },
  glossaryEntryNotes: {
    id: 'Entry notes',
    defaultMessage: 'Entry notes',
  },
  glossaryAdd: {
    id: 'Add glossary entry',
    defaultMessage: 'Add glossary entry',
  },
  glossaryRemove: {
    id: 'Remove',
    defaultMessage: 'Remove',
  },
  glossaryRulePreferred: {
    id: 'Preferred',
    defaultMessage: 'Preferred',
  },
  glossaryRuleRequired: {
    id: 'Required',
    defaultMessage: 'Required',
  },
  glossaryRuleAvoid: {
    id: 'Avoid',
    defaultMessage: 'Avoid',
  },
  glossaryTableHelp: {
    id: 'Use required for fixed client terminology, preferred for normal defaults, and avoid for terms you do not want in the final translation.',
    defaultMessage:
      'Use required for fixed client terminology, preferred for normal defaults, and avoid for terms you do not want in the final translation.',
  },
  clientReportInstructions: {
    id: 'Client report instructions',
    defaultMessage: 'Client report instructions',
  },
  translationTemplateDocx: {
    id: 'Translation template DOCX',
    defaultMessage: 'Translation template DOCX',
  },
  clientReportTemplateDocx: {
    id: 'Client report template DOCX',
    defaultMessage: 'Client report template DOCX',
  },
  docxOptional: {
    id: 'Optional. If omitted, export falls back to a simple generated DOCX.',
    defaultMessage:
      'Optional. If omitted, export falls back to a simple generated DOCX.',
  },
  saveProfile: {
    id: 'Save profile',
    defaultMessage: 'Save profile',
  },
  createProfile: {
    id: 'Create profile',
    defaultMessage: 'Create profile',
  },
  profileSaved: {
    id: 'OA profile saved.',
    defaultMessage: 'OA profile saved.',
  },
  profileCreated: {
    id: 'OA profile created.',
    defaultMessage: 'OA profile created.',
  },
  examplesTitle: {
    id: 'Translation examples',
    defaultMessage: 'Translation examples',
  },
  examplesNote: {
    id: 'Add example pairs using text, DOCX, or both. The translator will use the most relevant examples from the selected profile.',
    defaultMessage:
      'Add example pairs using text, DOCX, or both. The translator will use the most relevant examples from the selected profile.',
  },
  selectProfileFirst: {
    id: 'Select or create a profile before adding examples.',
    defaultMessage: 'Select or create a profile before adding examples.',
  },
  loadingExamples: {
    id: 'Loading examples…',
    defaultMessage: 'Loading examples…',
  },
  noExamples: {
    id: 'No examples yet.',
    defaultMessage: 'No examples yet.',
  },
  sourceDocumentDocx: {
    id: 'Source Japanese OA DOCX',
    defaultMessage: 'Source Japanese OA DOCX',
  },
  sourceText: {
    id: 'Source Japanese OA text',
    defaultMessage: 'Source Japanese OA text',
  },
  finalTranslationDocx: {
    id: 'Final English translation DOCX',
    defaultMessage: 'Final English translation DOCX',
  },
  finalTranslationText: {
    id: 'Final English translation text',
    defaultMessage: 'Final English translation text',
  },
  exampleTitle: {
    id: 'Example title',
    defaultMessage: 'Example title',
  },
  saveExample: {
    id: 'Save example',
    defaultMessage: 'Save example',
  },
  createExample: {
    id: 'Create example',
    defaultMessage: 'Create example',
  },
  exampleSaved: {
    id: 'OA translation example saved.',
    defaultMessage: 'OA translation example saved.',
  },
  exampleCreated: {
    id: 'OA translation example created.',
    defaultMessage: 'OA translation example created.',
  },
  useTextOrDocx: {
    id: 'Use text fields, DOCX uploads, or both. Text fields are easiest to edit later.',
    defaultMessage:
      'Use text fields, DOCX uploads, or both. Text fields are easiest to edit later.',
  },
  requestFailed: {
    id: 'Request failed with status {status}',
    defaultMessage: 'Request failed with status {status}',
  },
});

export default function OAProfilesPage() {
  const intl = useIntl();
  const token = useSelector((state) => state.userSession?.token);
  const [profiles, setProfiles] = useState([]);
  const [loadingProfiles, setLoadingProfiles] = useState(true);
  const [loadingProfileDetail, setLoadingProfileDetail] = useState(false);
  const [selectedProfilePath, setSelectedProfilePath] = useState('');
  const [profileForm, setProfileForm] = useState(initialProfileForm);
  const [translationTemplateFile, setTranslationTemplateFile] = useState(null);
  const [clientReportTemplateFile, setClientReportTemplateFile] = useState(null);
  const [savingProfile, setSavingProfile] = useState(false);
  const [examples, setExamples] = useState([]);
  const [loadingExamples, setLoadingExamples] = useState(false);
  const [selectedExamplePath, setSelectedExamplePath] = useState('');
  const [exampleForm, setExampleForm] = useState(initialExampleForm);
  const [sourceDocxFile, setSourceDocxFile] = useState(null);
  const [finalTranslationDocxFile, setFinalTranslationDocxFile] = useState(null);
  const [savingExample, setSavingExample] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    document.body.classList.add('patent-dashboard-page');
    return () => {
      document.body.classList.remove('patent-dashboard-page');
    };
  }, []);

  const selectedProfileSummary = useMemo(
    () => profiles.find((profile) => profile.path === selectedProfilePath) || null,
    [profiles, selectedProfilePath],
  );

  function authHeaders(extra = {}) {
    return token
      ? {
          ...extra,
          Authorization: `Bearer ${token}`,
        }
      : extra;
  }

  useEffect(() => {
    let active = true;

    async function loadProfiles() {
      try {
        const response = await fetch('/++api++/@oa-profiles', {
          headers: authHeaders({ Accept: 'application/json' }),
        });
        if (!response.ok) {
          throw new Error(await extractError(response, intl));
        }
        const payload = await response.json();
        if (!active) {
          return;
        }
        const items = payload.items || [];
        setProfiles(items);
        if (!selectedProfilePath && items.length) {
          setSelectedProfilePath(items[0].path);
        }
      } catch (loadError) {
        if (active) {
          setError(loadError.message);
        }
      } finally {
        if (active) {
          setLoadingProfiles(false);
        }
      }
    }

    loadProfiles();
    return () => {
      active = false;
    };
  }, [intl, token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let active = true;

    async function loadSelectedProfile() {
      if (!selectedProfilePath) {
        if (active) {
          setProfileForm(initialProfileForm);
          setExamples([]);
          setSelectedExamplePath('');
          setExampleForm(initialExampleForm);
        }
        return;
      }

      setLoadingProfileDetail(true);
      setLoadingExamples(true);
      try {
        const [profileResponse, examplesResponse] = await Promise.all([
          fetch(`/++api++${selectedProfilePath}`, {
            headers: authHeaders({ Accept: 'application/json' }),
          }),
          fetch(buildExampleSearchUrl(selectedProfilePath), {
            headers: authHeaders({ Accept: 'application/json' }),
          }),
        ]);
        if (!profileResponse.ok) {
          throw new Error(await extractError(profileResponse, intl));
        }
        if (!examplesResponse.ok) {
          throw new Error(await extractError(examplesResponse, intl));
        }
        const profilePayload = await profileResponse.json();
        const examplesPayload = await examplesResponse.json();
        if (!active) {
          return;
        }
        setProfileForm(normalizeProfile(profilePayload));
        setExamples((examplesPayload.items || []).map(normalizeExampleSearchItem));
        setSelectedExamplePath('');
        setExampleForm(initialExampleForm);
      } catch (loadError) {
        if (active) {
          setError(loadError.message);
        }
      } finally {
        if (active) {
          setLoadingProfileDetail(false);
          setLoadingExamples(false);
        }
      }
    }

    loadSelectedProfile();
    return () => {
      active = false;
    };
  }, [intl, selectedProfilePath, token]); // eslint-disable-line react-hooks/exhaustive-deps

  async function refreshProfiles(preferredPath = '') {
    const response = await fetch('/++api++/@oa-profiles', {
      headers: authHeaders({ Accept: 'application/json' }),
    });
    if (!response.ok) {
      throw new Error(await extractError(response, intl));
    }
    const payload = await response.json();
    const items = payload.items || [];
    setProfiles(items);
    if (preferredPath) {
      setSelectedProfilePath(preferredPath);
    } else if (!items.find((item) => item.path === selectedProfilePath)) {
      setSelectedProfilePath(items[0]?.path || '');
    }
    return items;
  }

  async function refreshExamples(preferredPath = '') {
    if (!selectedProfilePath) {
      setExamples([]);
      return [];
    }
    const response = await fetch(buildExampleSearchUrl(selectedProfilePath), {
      headers: authHeaders({ Accept: 'application/json' }),
    });
    if (!response.ok) {
      throw new Error(await extractError(response, intl));
    }
    const payload = await response.json();
    const items = (payload.items || []).map(normalizeExampleSearchItem);
    setExamples(items);
    if (preferredPath) {
      setSelectedExamplePath(preferredPath);
    }
    return items;
  }

  async function onSaveProfile(event) {
    event.preventDefault();
    setSavingProfile(true);
    setError('');
    setSuccess('');

    try {
      const payload = {
        title: profileForm.title,
        translation_style_instructions:
          profileForm.translation_style_instructions || '',
        preferred_terminology: serializePreferredTerminology(
          profileForm.glossary_entries,
          profileForm.terminology_notes,
        ),
        client_report_instructions: profileForm.client_report_instructions || '',
      };
      if (translationTemplateFile) {
        payload.translation_template_docx = await readFileField(translationTemplateFile);
      }
      if (clientReportTemplateFile) {
        payload.client_report_template_docx = await readFileField(
          clientReportTemplateFile,
        );
      }

      let profilePath = selectedProfilePath;
      let created = false;
      let response;
      if (selectedProfilePath) {
        response = await fetch(`/++api++${selectedProfilePath}`, {
          method: 'PATCH',
          headers: authHeaders({
            Accept: 'application/json',
            'Content-Type': 'application/json',
          }),
          body: JSON.stringify(payload),
        });
      } else {
        created = true;
        response = await fetch('/++api++/', {
          method: 'POST',
          headers: authHeaders({
            Accept: 'application/json',
            'Content-Type': 'application/json',
          }),
          body: JSON.stringify({
            '@type': 'OA Profile',
            ...payload,
          }),
        });
      }
      if (!response.ok) {
        throw new Error(await extractError(response, intl));
      }
      if (created) {
        const createdPayload = await response.json();
        profilePath = normalizeContentPath(createdPayload['@id'] || '');
      }
      await refreshProfiles(profilePath);
      setTranslationTemplateFile(null);
      setClientReportTemplateFile(null);
      setSuccess(
        intl.formatMessage(
          created ? messages.profileCreated : messages.profileSaved,
        ),
      );
    } catch (saveError) {
      setError(saveError.message);
    } finally {
      setSavingProfile(false);
    }
  }

  async function onLoadExample(examplePath) {
    setError('');
    setSuccess('');
    try {
      const response = await fetch(`/++api++${examplePath}`, {
        headers: authHeaders({ Accept: 'application/json' }),
      });
      if (!response.ok) {
        throw new Error(await extractError(response, intl));
      }
      const payload = await response.json();
      setSelectedExamplePath(examplePath);
      setExampleForm(normalizeExample(payload));
      setSourceDocxFile(null);
      setFinalTranslationDocxFile(null);
    } catch (loadError) {
      setError(loadError.message);
    }
  }

  async function onSaveExample(event) {
    event.preventDefault();
    if (!selectedProfilePath) {
      setError(intl.formatMessage(messages.selectProfileFirst));
      return;
    }
    setSavingExample(true);
    setError('');
    setSuccess('');

    try {
      const payload = {
        title: exampleForm.title,
        source_text: exampleForm.source_text || '',
        final_translation_text: exampleForm.final_translation_text || '',
        notes: exampleForm.notes || '',
      };
      if (sourceDocxFile) {
        payload.source_document_docx = await readFileField(sourceDocxFile);
      }
      if (finalTranslationDocxFile) {
        payload.final_translation_docx = await readFileField(
          finalTranslationDocxFile,
        );
      }

      let examplePath = selectedExamplePath;
      let created = false;
      let response;
      if (selectedExamplePath) {
        response = await fetch(`/++api++${selectedExamplePath}`, {
          method: 'PATCH',
          headers: authHeaders({
            Accept: 'application/json',
            'Content-Type': 'application/json',
          }),
          body: JSON.stringify(payload),
        });
      } else {
        created = true;
        response = await fetch(`/++api++${selectedProfilePath}`, {
          method: 'POST',
          headers: authHeaders({
            Accept: 'application/json',
            'Content-Type': 'application/json',
          }),
          body: JSON.stringify({
            '@type': 'OA Translation Example',
            ...payload,
          }),
        });
      }
      if (!response.ok) {
        throw new Error(await extractError(response, intl));
      }
      if (created) {
        const createdPayload = await response.json();
        examplePath = normalizeContentPath(createdPayload['@id'] || '');
      }
      await refreshProfiles(selectedProfilePath);
      await refreshExamples(examplePath);
      setSourceDocxFile(null);
      setFinalTranslationDocxFile(null);
      setSuccess(
        intl.formatMessage(
          created ? messages.exampleCreated : messages.exampleSaved,
        ),
      );
    } catch (saveError) {
      setError(saveError.message);
    } finally {
      setSavingExample(false);
    }
  }

  function startNewProfile() {
    setSelectedProfilePath('');
    setProfileForm(initialProfileForm);
    setTranslationTemplateFile(null);
    setClientReportTemplateFile(null);
    setSelectedExamplePath('');
    setExampleForm(initialExampleForm);
    setExamples([]);
    setError('');
    setSuccess('');
  }

  function startNewExample() {
    setSelectedExamplePath('');
    setExampleForm(initialExampleForm);
    setSourceDocxFile(null);
    setFinalTranslationDocxFile(null);
    setError('');
    setSuccess('');
  }

  return (
    <main className="patent-entry">
      <section className="patent-entry__shell">
        <AppNav currentPath="/tools" />
        <header className="patent-entry__hero">
          <p className="patent-entry__eyebrow">
            {intl.formatMessage(messages.pageEyebrow)}
          </p>
          <h1>{intl.formatMessage(messages.pageTitle)}</h1>
          <p className="patent-entry__note">
            {intl.formatMessage(messages.pageNote)}
          </p>
          <div className="patent-entry__actions patent-entry__actions--row">
            <Link className="patent-entry__ghost" to="/tools">
              {intl.formatMessage(sharedMessages.backToTools)}
            </Link>
            <button
              className="patent-entry__ghost"
              type="button"
              onClick={startNewProfile}
            >
              {intl.formatMessage(messages.newProfile)}
            </button>
          </div>
        </header>

        {loadingProfiles ? (
          <p className="patent-entry__note">
            {intl.formatMessage(messages.loadingProfiles)}
          </p>
        ) : null}
        {error ? <p className="patent-entry__error">{error}</p> : null}
        {success ? <p className="patent-entry__success">{success}</p> : null}

        <div className="patent-entry__split">
          <section className="patent-entry__card">
            <div className="patent-entry__section-header">
              <div>
                <h2>{intl.formatMessage(messages.profiles)}</h2>
              </div>
            </div>
            {!profiles.length ? (
              <p className="patent-entry__note">
                {intl.formatMessage(messages.noProfiles)}
              </p>
            ) : (
              <ul className="patent-entry__pending-jobs-list">
                {profiles.map((profile) => (
                  <li
                    key={profile.path}
                    className="patent-entry__pending-job"
                  >
                    <button
                      className="patent-entry__pending-job-title patent-entry__pending-job-title--button"
                      type="button"
                      onClick={() => setSelectedProfilePath(profile.path)}
                    >
                      {profile.title}
                    </button>
                    <span className="patent-entry__pending-job-meta">
                      {intl.formatMessage(messages.exampleCount, {
                        count: profile.translation_example_count || 0,
                      })}
                      {' · '}
                      {profile.has_translation_template
                        ? intl.formatMessage(messages.translationTemplateReady)
                        : intl.formatMessage(messages.translationTemplateMissing)}
                      {' · '}
                      {profile.has_client_report_template
                        ? intl.formatMessage(messages.clientReportTemplateReady)
                        : intl.formatMessage(messages.clientReportTemplateMissing)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="patent-entry__card">
            <div className="patent-entry__section-header">
              <div>
                <h2>{intl.formatMessage(messages.profileEditor)}</h2>
                {selectedProfileSummary ? (
                  <p className="patent-entry__note">
                    {intl.formatMessage(messages.selectedProfile, {
                      title: selectedProfileSummary.title,
                    })}
                  </p>
                ) : null}
              </div>
            </div>
            {loadingProfileDetail ? (
              <p className="patent-entry__note">
                {intl.formatMessage(sharedMessages.loading)}
              </p>
            ) : null}
            <form className="patent-entry__form" onSubmit={onSaveProfile}>
              <TextField
                label={intl.formatMessage(messages.profileTitle)}
                value={profileForm.title}
                required
                onChange={(value) =>
                  setProfileForm((current) => ({ ...current, title: value }))
                }
              />
              <TextAreaField
                label={intl.formatMessage(messages.translationStyleInstructions)}
                rows={8}
                value={profileForm.translation_style_instructions}
                onChange={(value) =>
                  setProfileForm((current) => ({
                    ...current,
                    translation_style_instructions: value,
                  }))
                }
              />
              <div className="patent-entry__field patent-entry__field--wide">
                <span className="patent-entry__label">
                  {intl.formatMessage(messages.preferredTerminology)}
                </span>
                <span className="patent-entry__help">
                  {intl.formatMessage(messages.terminologyDictionaryNote)}
                </span>
                <div className="patent-entry__glossary-table">
                  <div className="patent-entry__glossary-head">
                    <span>{intl.formatMessage(messages.glossaryJapanese)}</span>
                    <span>{intl.formatMessage(messages.glossaryEnglish)}</span>
                    <span>{intl.formatMessage(messages.glossaryRule)}</span>
                    <span>{intl.formatMessage(messages.glossaryEntryNotes)}</span>
                    <span aria-hidden="true" />
                  </div>
                  {profileForm.glossary_entries.map((entry, index) => (
                    <div key={`glossary-${index}`} className="patent-entry__glossary-row">
                      <input
                        className="patent-entry__input"
                        value={entry.japanese}
                        onChange={(event) =>
                          setProfileForm((current) => ({
                            ...current,
                            glossary_entries: current.glossary_entries.map(
                              (currentEntry, currentIndex) =>
                                currentIndex === index
                                  ? {
                                      ...currentEntry,
                                      japanese: event.target.value,
                                    }
                                  : currentEntry,
                            ),
                          }))
                        }
                      />
                      <input
                        className="patent-entry__input"
                        value={entry.english}
                        onChange={(event) =>
                          setProfileForm((current) => ({
                            ...current,
                            glossary_entries: current.glossary_entries.map(
                              (currentEntry, currentIndex) =>
                                currentIndex === index
                                  ? {
                                      ...currentEntry,
                                      english: event.target.value,
                                    }
                                  : currentEntry,
                            ),
                          }))
                        }
                      />
                      <select
                        className="patent-entry__input"
                        value={entry.rule}
                        onChange={(event) =>
                          setProfileForm((current) => ({
                            ...current,
                            glossary_entries: current.glossary_entries.map(
                              (currentEntry, currentIndex) =>
                                currentIndex === index
                                  ? {
                                      ...currentEntry,
                                      rule: event.target.value,
                                    }
                                  : currentEntry,
                            ),
                          }))
                        }
                      >
                        <option value="preferred">
                          {intl.formatMessage(messages.glossaryRulePreferred)}
                        </option>
                        <option value="required">
                          {intl.formatMessage(messages.glossaryRuleRequired)}
                        </option>
                        <option value="avoid">
                          {intl.formatMessage(messages.glossaryRuleAvoid)}
                        </option>
                      </select>
                      <input
                        className="patent-entry__input"
                        value={entry.notes}
                        onChange={(event) =>
                          setProfileForm((current) => ({
                            ...current,
                            glossary_entries: current.glossary_entries.map(
                              (currentEntry, currentIndex) =>
                                currentIndex === index
                                  ? {
                                      ...currentEntry,
                                      notes: event.target.value,
                                    }
                                  : currentEntry,
                            ),
                          }))
                        }
                      />
                      <button
                        className="patent-entry__ghost patent-entry__glossary-remove"
                        type="button"
                        onClick={() =>
                          setProfileForm((current) => ({
                            ...current,
                            glossary_entries:
                              current.glossary_entries.length > 1
                                ? current.glossary_entries.filter(
                                    (_, currentIndex) => currentIndex !== index,
                                  )
                                : [createEmptyGlossaryEntry()],
                          }))
                        }
                      >
                        {intl.formatMessage(messages.glossaryRemove)}
                      </button>
                    </div>
                  ))}
                </div>
                <span className="patent-entry__help">
                  {intl.formatMessage(messages.glossaryTableHelp)}
                </span>
                <div className="patent-entry__actions patent-entry__actions--row">
                  <button
                    className="patent-entry__ghost"
                    type="button"
                    onClick={() =>
                      setProfileForm((current) => ({
                        ...current,
                        glossary_entries: [
                          ...current.glossary_entries,
                          createEmptyGlossaryEntry(),
                        ],
                      }))
                    }
                  >
                    {intl.formatMessage(messages.glossaryAdd)}
                  </button>
                </div>
              </div>
              <TextAreaField
                label={intl.formatMessage(messages.terminologyNotes)}
                rows={5}
                value={profileForm.terminology_notes}
                onChange={(value) =>
                  setProfileForm((current) => ({
                    ...current,
                    terminology_notes: value,
                  }))
                }
              />
              <FileField
                label={intl.formatMessage(messages.translationTemplateDocx)}
                helpText={intl.formatMessage(messages.docxOptional)}
                onChange={setTranslationTemplateFile}
              />
              <TextAreaField
                label={intl.formatMessage(messages.clientReportInstructions)}
                rows={8}
                value={profileForm.client_report_instructions}
                onChange={(value) =>
                  setProfileForm((current) => ({
                    ...current,
                    client_report_instructions: value,
                  }))
                }
              />
              <FileField
                label={intl.formatMessage(messages.clientReportTemplateDocx)}
                helpText={intl.formatMessage(messages.docxOptional)}
                onChange={setClientReportTemplateFile}
              />
              <div className="patent-entry__actions patent-entry__actions--row">
                <button
                  className="patent-entry__submit"
                  type="submit"
                  disabled={savingProfile || !profileForm.title.trim()}
                >
                  {savingProfile
                    ? intl.formatMessage(sharedMessages.saving)
                    : selectedProfilePath
                      ? intl.formatMessage(messages.saveProfile)
                      : intl.formatMessage(messages.createProfile)}
                </button>
              </div>
            </form>
          </section>
        </div>

        <section className="patent-entry__card">
          <div className="patent-entry__section-header">
            <div>
              <h2>{intl.formatMessage(messages.examplesTitle)}</h2>
              <p className="patent-entry__note">
                {selectedProfilePath
                  ? intl.formatMessage(messages.examplesNote)
                  : intl.formatMessage(messages.selectProfileFirst)}
              </p>
            </div>
            <button
              className="patent-entry__ghost"
              type="button"
              onClick={startNewExample}
              disabled={!selectedProfilePath}
            >
              {intl.formatMessage(messages.createExample)}
            </button>
          </div>

          {loadingExamples ? (
            <p className="patent-entry__note">
              {intl.formatMessage(messages.loadingExamples)}
            </p>
          ) : examples.length ? (
            <ul className="patent-entry__pending-jobs-list">
              {examples.map((example) => (
                <li key={example.path} className="patent-entry__pending-job">
                  <button
                    className="patent-entry__pending-job-title patent-entry__pending-job-title--button"
                    type="button"
                    onClick={() => onLoadExample(example.path)}
                  >
                    {example.title}
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="patent-entry__note">
              {intl.formatMessage(
                selectedProfilePath ? messages.noExamples : messages.selectProfileFirst,
              )}
            </p>
          )}

          {selectedProfilePath ? (
            <form className="patent-entry__form" onSubmit={onSaveExample}>
              <TextField
                label={intl.formatMessage(messages.exampleTitle)}
                value={exampleForm.title}
                required
                onChange={(value) =>
                  setExampleForm((current) => ({ ...current, title: value }))
                }
              />
              <FileField
                label={intl.formatMessage(messages.sourceDocumentDocx)}
                helpText={intl.formatMessage(messages.useTextOrDocx)}
                onChange={setSourceDocxFile}
              />
              <TextAreaField
                label={intl.formatMessage(messages.sourceText)}
                rows={12}
                value={exampleForm.source_text}
                onChange={(value) =>
                  setExampleForm((current) => ({ ...current, source_text: value }))
                }
              />
              <FileField
                label={intl.formatMessage(messages.finalTranslationDocx)}
                helpText={intl.formatMessage(messages.useTextOrDocx)}
                onChange={setFinalTranslationDocxFile}
              />
              <TextAreaField
                label={intl.formatMessage(messages.finalTranslationText)}
                rows={12}
                value={exampleForm.final_translation_text}
                onChange={(value) =>
                  setExampleForm((current) => ({
                    ...current,
                    final_translation_text: value,
                  }))
                }
              />
              <TextAreaField
                label={intl.formatMessage(sharedMessages.notes)}
                rows={6}
                value={exampleForm.notes}
                onChange={(value) =>
                  setExampleForm((current) => ({ ...current, notes: value }))
                }
              />
              <div className="patent-entry__actions patent-entry__actions--row">
                <button
                  className="patent-entry__submit"
                  type="submit"
                  disabled={savingExample || !exampleForm.title.trim()}
                >
                  {savingExample
                    ? intl.formatMessage(sharedMessages.saving)
                    : selectedExamplePath
                      ? intl.formatMessage(messages.saveExample)
                      : intl.formatMessage(messages.createExample)}
                </button>
              </div>
            </form>
          ) : null}
        </section>
      </section>
    </main>
  );
}

function TextField({ label, value, onChange, required = false }) {
  return (
    <label className="patent-entry__field">
      <span className="patent-entry__label">{label}</span>
      <input
        className="patent-entry__input"
        value={value}
        required={required}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  );
}

function TextAreaField({ label, value, onChange, rows = 6 }) {
  return (
    <label className="patent-entry__field patent-entry__field--wide">
      <span className="patent-entry__label">{label}</span>
      <textarea
        className="patent-entry__textarea"
        rows={rows}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  );
}

function FileField({ label, helpText, onChange }) {
  return (
    <label className="patent-entry__field patent-entry__field--wide">
      <span className="patent-entry__label">{label}</span>
      <input
        className="patent-entry__input"
        type="file"
        accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        onChange={(event) => onChange(event.target.files?.[0] || null)}
      />
      {helpText ? <span className="patent-entry__help">{helpText}</span> : null}
    </label>
  );
}

function normalizeContentPath(path) {
  if (!path) {
    return '';
  }
  const origin =
    typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
  try {
    return new URL(path, origin).pathname;
  } catch {
    return path;
  }
}

function normalizeProfile(payload) {
  const terminology = parsePreferredTerminology(payload.preferred_terminology);
  return {
    title: payload.title || '',
    translation_style_instructions: payload.translation_style_instructions || '',
    terminology_notes: terminology.notes,
    glossary_entries: terminology.entries.length
      ? terminology.entries
      : [createEmptyGlossaryEntry()],
    client_report_instructions: payload.client_report_instructions || '',
  };
}

function normalizeExample(payload) {
  return {
    title: payload.title || '',
    source_text: payload.source_text || '',
    final_translation_text: payload.final_translation_text || '',
    notes: payload.notes || '',
  };
}

function normalizeExampleSearchItem(item) {
  return {
    path: normalizeContentPath(item['@id'] || item.getURL || ''),
    title: item.title || '',
  };
}

function buildExampleSearchUrl(profilePath) {
  const query = new URLSearchParams({
    portal_type: 'OA Translation Example',
    b_size: '200',
    'path.query': profilePath,
    'path.depth': '1',
  });
  query.append('metadata_fields', 'title');
  return `/++api++/@search?${query.toString()}`;
}

async function readFileField(file) {
  const data = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result || '');
    reader.onerror = () => reject(reader.error || new Error('File read failed.'));
    reader.readAsDataURL(file);
  });

  const [, base64] = String(data).split(',', 2);
  return {
    filename: file.name,
    data: base64 || '',
    encoding: 'base64',
    'content-type': file.type || DOCX_MIME,
  };
}

async function extractError(response, intl) {
  try {
    const payload = await response.json();
    return (
      payload.message ||
      payload.error?.message ||
      (typeof payload.error === 'string' ? payload.error : '') ||
      intl.formatMessage(messages.requestFailed, { status: response.status })
    );
  } catch {
    return intl.formatMessage(messages.requestFailed, { status: response.status });
  }
}

function createEmptyGlossaryEntry() {
  return {
    japanese: '',
    english: '',
    rule: 'preferred',
    notes: '',
  };
}

function normalizeGlossaryEntry(entry) {
  const japanese = String(
    entry?.japanese || entry?.source || entry?.term || entry?.ja || '',
  ).trim();
  const english = String(
    entry?.english || entry?.target || entry?.translation || entry?.en || '',
  ).trim();
  const notes = String(entry?.notes || '').trim();
  const rule = ['required', 'preferred', 'avoid'].includes(entry?.rule)
    ? entry.rule
    : 'preferred';

  if (!japanese || !english) {
    return null;
  }

  return {
    japanese,
    english,
    rule,
    notes,
  };
}

function parsePreferredTerminology(value) {
  const raw = String(value || '').trim();
  if (!raw) {
    return { entries: [], notes: '' };
  }

  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return {
        entries: parsed.map(normalizeGlossaryEntry).filter(Boolean),
        notes: '',
      };
    }
    if (typeof parsed === 'string') {
      return parseLegacyPreferredTerminology(parsed);
    }
    if (parsed && typeof parsed === 'object') {
      return {
        entries: (parsed.entries || []).map(normalizeGlossaryEntry).filter(Boolean),
        notes: String(parsed.notes || '').trim(),
      };
    }
  } catch {
    return parseLegacyPreferredTerminology(raw);
  }

  return { entries: [], notes: '' };
}

function parseLegacyPreferredTerminology(raw) {
  const entries = [];
  const notes = [];
  const separators = ['=>', '->', '→', '\t'];

  String(raw || '')
    .split(/\r?\n/u)
    .forEach((line) => {
      const trimmed = line.trim();
      if (!trimmed) {
        return;
      }

      let matched = false;
      separators.forEach((separator) => {
        if (matched || !trimmed.includes(separator)) {
          return;
        }
        const [left, right] = trimmed.split(separator, 2);
        const entry = normalizeGlossaryEntry({
          japanese: left.replace(/^[*-]\s*/u, ''),
          english: right,
          rule: 'preferred',
        });
        if (entry) {
          entries.push(entry);
          matched = true;
        }
      });

      if (!matched) {
        notes.push(trimmed);
      }
    });

  return {
    entries,
    notes: notes.join('\n').trim(),
  };
}

function serializePreferredTerminology(entries, notes) {
  const normalizedEntries = (entries || [])
    .map(normalizeGlossaryEntry)
    .filter(Boolean);
  const normalizedNotes = String(notes || '').trim();
  if (!normalizedEntries.length && !normalizedNotes) {
    return '';
  }
  return JSON.stringify(
    {
      entries: normalizedEntries,
      notes: normalizedNotes,
    },
    null,
    2,
  );
}
